"use client"

import React, { useState } from "react"
import { Button } from "../ui/button"
import { FormInput } from "../ui/form-input"
import { Mail, AlertCircle, CheckCircle } from "lucide-react"
import { Link, useNavigate } from "react-router-dom"
import { authService } from "../../apis/authService"

export function ForgotPasswordForm() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setMessage({ type: "error", text: "Email is required" });
      return;
    }
    
    if (!/\S+@\S+\.\S+/.test(email)) {
      setMessage({ type: "error", text: "Invalid email format" });
      return;
    }
    
    setIsLoading(true);
    setMessage(null);
    
    try {
      const result = await authService.requestPasswordReset(email);
      
      if (result.success) {
        setMessage({ 
          type: "success", 
          text: result.message 
        });
      } else {
        setMessage({ type: "error", text: result.message });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Request failed. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${message.type === "success" ? 
          "bg-teal-50 text-teal-600 border border-teal-200" : 
          "bg-red-50 text-red-800 border border-red-200"}`}
        >
          {message.type === "success" ? 
            <CheckCircle className="w-5 h-5 text-teal-600" /> : 
            <AlertCircle className="w-5 h-5 text-red-600" />}
          <span className="text-sm font-medium">{message.text}</span>
        </div>
      )}

      <div className="text-center">
        <div className="mx-auto bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mb-4">
          <Mail className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Forgot your password?</h2>
        <p className="mt-2 text-sm text-gray-600">
          Enter your email to receive a password reset link
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <FormInput
          label="Email Address"
          type="email"
          placeholder="doctor@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          icon={<Mail className="w-5 h-5" />}
          required
        />

        <Button
          type="submit"
          size="lg"
          className="w-full h-[52px] rounded-lg bg-gradient-to-r from-blue-600 to-teal-600 hover:shadow-lg text-white font-medium transition disabled:opacity-70"
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Sending reset link...</span>
            </div>
          ) : (
            "Send Reset Link"
          )}
        </Button>
      </form>

      <div className="text-center mt-4">
        <p className="text-sm text-gray-600">
          Remember your password?{" "}
          <Link to="/signin" className="text-blue-600 hover:text-blue-700 font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}